// src/components/modal/CreditoDetallesModal.jsx
import { useState, useEffect } from "react";
import { doc, updateDoc, arrayUnion, getDoc, onSnapshot } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseconfig";
import { generarCreditoPDF } from "../reports/PDFCredito";
import logo2Url from "../../assets/logo2.png";
import uploadService from "../../services/uploadService";
import { FileText, Download, Upload, MessageSquare, CheckCircle, XCircle, Clock, AlertTriangle, Paperclip, FolderOpen } from "lucide-react";


export default function CreditoDetallesModal({
  credito,
  isAdmin = false,
  onClose,
  onGenerarPDF,     // sigue siendo opcional; si no viene, usamos nuestro generador
  onMarcarPagado,
  onMarcarFaltaPago
}) {
  const [comentarios, setComentarios] = useState("");
  const [historialComentarios, setHistorialComentarios] = useState([]);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  
  const [logoBase64, setLogoBase64] = useState(null);

  const sortByFechaDesc = (arr = []) =>
  [...arr].sort((a, b) => {
    const fa = new Date(a.fechaCreacion || a.fecha || 0).getTime();
    const fb = new Date(b.fechaCreacion || b.fecha || 0).getTime();
    return fb - fa; // más nuevos primero
  });


  const formatCurrency = (n) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 0 }).format(n || 0);
  const formatDate = (d) => { 
    try { 
      return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit", 
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch { 
      return "N/A"; 
    } 
  };
 const tipoLegible = (t) => {
  const s = String(t||"").toLowerCase();
  if (s.includes("hipotec")) return "Hipotecario";
  if (s.includes("prend"))   return "Prendario";
  if (s.includes("person"))  return "Personal";
  return "—";
};


  const estadoInfo = (estado) => {
    const e = (estado || "").toLowerCase();
    if (e === "aprobada")  return { icon: <CheckCircle size={44} color="#10b981" />, titulo: "Aprobada",      sub: "Crédito aprobado y vigente.",              cls: "ok" };
    if (e === "rechazada") return { icon: <XCircle size={44} color="#ef4444" />,     titulo: "Rechazada",     sub: "La solicitud fue rechazada.",               cls: "bad" };
    if (e === "revision")  return { icon: <Clock size={44} color="#f59e0b" />,        titulo: "En revisión",   sub: "Estamos evaluando la solicitud.",           cls: "warn" };
    if (e === "faltapago") return { icon: <AlertTriangle size={44} color="#dc2626" />,titulo: "Falta de pago", sub: "Hay cuotas vencidas.",                     cls: "danger" };
    return                        { icon: <Clock size={44} color="#9ca3af" />,        titulo: "Pendiente",     sub: "La solicitud está pendiente de revisión.", cls: "pending" };
  };
  const EST = estadoInfo(credito?.estado);

  // Función para inferir la colección
function inferColeccion(cred) {
    // Respeta hints explícitos
    if (cred?.coleccion) return cred.coleccion;
    if (cred?.collectionName) return cred.collectionName;
    if (cred?.origen === "creditos") return "creditos";

    // Heurística por tipo
    const t = String(cred?.tipo || "").toLowerCase().trim();
    if (t.includes("hipotec")) return "creditosHipotecarios";
    if (t.includes("prend"))   return "creditosPrendarios";
    if (t.includes("person"))  return "creditosPersonales";
    // Fallback seguro cuando viene desde la grilla de créditos aprobados
    return "creditos";
  }

  // Cargar historial de comentarios
const cargarHistorialComentarios = async () => {
  try {
    setLoadingHistorial(true);
    const col = inferColeccion(credito);
    const id  = String(credito?.id || "").trim();
    if (!col || !id) return setHistorialComentarios([]);

    const snap = await getDoc(doc(db, col, id));
    if (snap.exists()) {
      const data = snap.data() || {};
      const hist = Array.isArray(data.comentariosHistorial) ? data.comentariosHistorial : [];
      setHistorialComentarios(sortByFechaDesc(hist));
    } else {
      setHistorialComentarios([]);
    }
  } catch (error) {
    console.error("Error cargando historial de comentarios:", error);
    setHistorialComentarios([]);
  } finally {
    setLoadingHistorial(false);
  }
};

  // Guardar nuevo comentario (sin archivos)
const guardarComentario = async () => {
    if (!isAdmin) {
      alert("Solo el administrador puede agregar comentarios internos.");
      return;
    }
    if (!comentarios.trim()) {
      alert("Por favor escribe un comentario");
      return;
    }

    try {
      setSaving(true);
      const id = String(credito?.id || credito?.docId || "").trim();
      if (!id) throw new Error("Documento sin ID.");

      const nuevoComentario = {
        id: Date.now().toString(),
        texto: comentarios.trim(),
        fechaCreacion: new Date().toISOString(),
        autor: "Admin", // o el displayName/email del user logueado
      };

      // Vamos a intentar varias colecciones: prioriza 'creditos' y luego la inferida por tipo
      const candidatos = Array.from(
        new Set([
          "creditos",
          inferColeccion(credito), // puede devolver 'creditos' o la tipo-específica
        ])
      ).filter(Boolean);

      let actualizado = false;
      let ultimoError = null;
      for (const col of candidatos) {
        try {
          await updateDoc(doc(db, col, id), {
            comentariosHistorial: arrayUnion(nuevoComentario),
            ultimoComentario: nuevoComentario.texto,
            fechaUltimoComentario: nuevoComentario.fechaCreacion,
          });
          actualizado = true;
          break;
        } catch (e) {
          ultimoError = e;
        }
      }
      if (!actualizado) throw ultimoError || new Error("No se pudo actualizar el documento");

      // UI local
      setComentarios("");
      alert("✅ Comentario guardado");
    } catch (e) {
      console.error("❌ Error guardando comentario:", e);
      alert("❌ Error guardando comentario: " + (e.message || "Error desconocido"));
    } finally {
      setSaving(false);
    }
  };

  // Función separada para subir archivos al crédito
  const subirArchivos = async () => {
    if (archivosAdjuntos.length === 0) {
      alert("Por favor selecciona archivos para subir");
      return;
    }

    try {
      setUploading(true);
      const col = inferColeccion(credito);
      const id = String(credito?.id || "").trim();

      if (!col || !id) {
        alert("❌ No se pudo determinar dónde guardar los archivos.");
        return;
      }

      console.log("📤 Subiendo archivos al crédito:", archivosAdjuntos.length);
      
      const ruta = `creditos/${id}/archivos-adicionales`;
      const resultado = await uploadService.subirMultiplesArchivos(archivosAdjuntos, ruta);
      
      if (resultado.exitosos.length > 0) {
        console.log("✅ Archivos subidos exitosamente:", resultado.exitosos);

        // Guardar los archivos en el documento del crédito
        const archivosParaGuardar = resultado.exitosos.map(archivo => {
          const archivoData = {
            // ⭐ IMPORTANTE: Usar 'nombre_original' que preserva el nombre sin timestamp
            nombre: archivo.nombre_original || archivo.nombre || archivo.name || "archivo-sin-nombre",
            url: archivo.url || null,
            fechaSubida: new Date().toISOString(),
            tipo: archivo.tipo || archivo.type || "archivo",
            tamano: archivo.tamano || archivo.size || 0,
            // Información adicional para debugging
            extension: archivo.extension || null,
            isDevelopmentMock: archivo.isDevelopmentMock || false
          };
          
          console.log(`📝 Guardando archivo: ${archivoData.nombre} (${archivoData.tipo}, ${archivoData.tamano} bytes)`);
          return archivoData;
        });

        // Obtener archivos adicionales actuales y agregar los nuevos
        const archivosActuales = credito.archivosAdicionales || [];
        const nuevosArchivos = [...archivosActuales, ...archivosParaGuardar];

        await updateDoc(doc(db, col, id), {
          archivosAdicionales: nuevosArchivos,
          fechaUltimaActualizacion: new Date().toISOString(),
        });

        console.log(`✅ ${resultado.exitosos.length} archivo(s) guardado(s) en Firestore`);
        alert(`✅ ${resultado.exitosos.length} archivo(s) subido(s) correctamente al crédito`);
        setArchivosAdjuntos([]);

        // Actualizar crédito localmente para reflejar los nuevos archivos
        credito.archivosAdicionales = nuevosArchivos;
      }

      if (resultado.errores.length > 0) {
        console.warn('⚠️ Algunos archivos no se pudieron subir:', resultado.errores);
        const erroresTexto = resultado.errores.map(e => e.error).join(', ');
        if (resultado.exitosos.length === 0) {
          throw new Error(`No se pudieron subir los archivos: ${erroresTexto}`);
        } else {
          alert(`⚠️ Se subieron ${resultado.exitosos.length} de ${archivosAdjuntos.length} archivos.\nErrores: ${erroresTexto}`);
        }
      }

    } catch (error) {
      console.error("❌ Error subiendo archivos:", error);
      alert(`❌ Error al subir archivos: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Manejar selección de archivos (solo preparar, no subir aún)
  const handleFileChange = async (files) => {
    if (!files || files.length === 0) return;

    console.log("📁 Seleccionando archivos:", files.length);

    try {
      const validFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        console.log(`📄 Validando archivo ${i + 1}: ${file.name} (${file.type}, ${file.size} bytes)`);
        
        // Validar tipo de archivo
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
          console.warn(`❌ Tipo de archivo no válido: ${file.type}`);
          alert(`El archivo ${file.name} no es válido. Solo se permiten archivos PDF, JPG, PNG, TXT, DOC, DOCX`);
          continue;
        }
        
        // Validar tamaño (máximo 10MB)
        if (file.size > 10 * 1024 * 1024) {
          console.warn(`❌ Archivo muy grande: ${file.size} bytes`);
          alert(`El archivo ${file.name} supera los 10MB permitidos`);
          continue;
        }
        
        console.log(`✅ Archivo válido: ${file.name}`);
        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        console.log(`📋 Agregando ${validFiles.length} archivos válidos a la cola`);
        setArchivosAdjuntos(prev => [...prev, ...validFiles]);
        alert(`✅ ${validFiles.length} archivo(s) seleccionado(s). Haz clic en "Subir archivos" para subirlos.`);
      }
    } catch (error) {
      console.error('❌ Error procesando archivos:', error);
      alert('❌ Error al procesar archivos');
    }
  };

  // Remover archivo adjunto
  const removeFile = (index) => {
    setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index));
  };

  // Función para mapear tipos de documentos a rutas de Storage
  const mapearTipoAPath = (tipo) => {
    const mapeo = {
      'reciboSueldo': 'recibos-sueldo',
      'fotoDNI': 'fotos-dni',
      'escrituras': 'escrituras'
    };
    return mapeo[tipo] || tipo;
  };

  // Función para obtener el prefijo de ruta según el tipo de crédito
  const obtenerPrefijoRuta = (tipoCredito) => {
    const tipo = String(tipoCredito || "").toLowerCase().trim();
    
    if (tipo.includes("hipotec")) return "credito-hipotecario";
    if (tipo.includes("prend")) return "credito-prendario";
    if (tipo.includes("person")) return "credito-personal";
    
    // Fallback: intentar usar el tipo directamente
    return `credito-${tipo}`;
  };

  // Función para intentar obtener URL desde Firebase Storage
  const obtenerURLDesdeStorage = async (storageRef) => {
    try {
      console.log(`🔄 Intentando obtener URL desde Storage: ${storageRef}`);
      const storageReference = ref(storage, storageRef);
      const url = await getDownloadURL(storageReference);
      console.log(`✅ URL obtenida desde Storage: ${url}`);
      return url;
    } catch (error) {
      console.log(`❌ No se pudo obtener URL desde Storage: ${error.message}`);
      
      // Si es un error de CORS en desarrollo, intentar construir URL directa
      if (error.message.includes('CORS') || error.code === 'storage/unauthorized') {
        console.log('🔄 Intentando construir URL directa para desarrollo...');
        try {
          // Construir URL directa de Firebase Storage
          const bucket = 'gamaan-fbc65.appspot.com';
          const encodedPath = encodeURIComponent(storageRef);
          const directUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
          console.log(`🔗 URL directa construida: ${directUrl}`);
          return directUrl;
        } catch (directError) {
          console.log(`❌ Error construyendo URL directa: ${directError.message}`);
        }
      }
      
      return null;
    }
  };

  // Obtener archivos originales del crédito
  const getArchivosOriginales = () => {
    console.log("🔍 Analizando archivos de crédito:", credito?.archivos);
    console.log("📋 Tipo de crédito:", credito?.tipo);
    console.log("🗂️ Prefijo de ruta calculado:", obtenerPrefijoRuta(credito?.tipo));
    
    const archivos = [];
    
    if (credito?.archivos) {
      Object.entries(credito.archivos).forEach(([tipo, info]) => {
        console.log(`📂 Procesando tipo: ${tipo}`, info);
        
        // Filtrar solo los campos que NO terminan en "URLs" y contienen archivos
        if (tipo.endsWith('URLs') || tipo.includes('URL')) {
          console.log(`⏭️ Saltando campo de URLs: ${tipo}`);
          return;
        }
        
        let nombres = [];
        let urlsCorrespondientes = [];
        
        // Manejar diferentes estructuras de datos
        if (Array.isArray(info)) {
          // Estructura simple: fotoDNI: ['archivo1.pdf', 'archivo2.jpg']
          nombres = info;
          // Buscar URLs correspondientes en el campo con sufijo URLs
          const campoURLs = `${tipo}URLs`;
          urlsCorrespondientes = credito.archivos[campoURLs] || [];
          console.log(`📋 Estructura array: ${nombres.length} archivos, ${urlsCorrespondientes.length} URLs`);
        } else if (info && typeof info === 'object' && info.nombres && Array.isArray(info.nombres)) {
          // Estructura compleja: fotoDNI: { nombres: ['archivo.png'], cantidad: 1, subidos: 1 }
          nombres = info.nombres;
          // Buscar URLs en diferentes campos posibles
          urlsCorrespondientes = info.urls || info.urlsSubidos || info[`${tipo}URLs`] || [];
          console.log(`📋 Estructura objeto: ${nombres.length} archivos, ${urlsCorrespondientes.length} URLs`);
        } else {
          console.log(`⏭️ Estructura no reconocida para ${tipo}:`, info);
          return;
        }
        
        // Solo procesar si hay nombres de archivos
        if (nombres.length === 0) {
          console.log(`⏭️ No hay archivos en ${tipo}`);
          return;
        }
        
        nombres.forEach((nombre, index) => {
          // Obtener URL correspondiente al índice
          const url = urlsCorrespondientes[index] || null;
          
          const archivoData = {
            nombre,
            tipo: tipo,
            esOriginal: true,
            categoria: "Documento original",
            url: url,
            // Mantener datos adicionales para debugging
            infoCompleta: info,
            // Construir la ruta correcta según el patrón del formulario y tipo de crédito
            storageRef: `${obtenerPrefijoRuta(credito.tipo)}/${mapearTipoAPath(tipo)}/${nombre}`
          };
          
          console.log(`📄 Archivo ${index + 1}:`, archivoData);
          console.log(`🔗 URL encontrada: ${url ? 'SÍ' : 'NO'} - ${url ? url.substring(0, 50) + '...' : 'null'}`);
          console.log(`📁 Storage Ref construida: ${archivoData.storageRef}`);
          archivos.push(archivoData);
        });
      });
    }
    
    console.log("📋 Total archivos originales:", archivos.length);
    return archivos;
  };

  // Obtener archivos adicionales subidos posteriormente
  const getArchivosAdicionales = () => {
    console.log("🔍 Analizando archivos adicionales:", credito?.archivosAdicionales);
    const archivos = [];
    
    if (credito?.archivosAdicionales && Array.isArray(credito.archivosAdicionales)) {
      credito.archivosAdicionales.forEach((archivo, index) => {
        const archivoData = {
          nombre: archivo.nombre,
          tipo: archivo.tipo || "Archivo adicional",
          esOriginal: false,
          categoria: "Archivo adicional",
          url: archivo.url,
          fechaSubida: archivo.fechaSubida,
          tamano: archivo.tamano
        };
        
        console.log(`📎 Archivo adicional ${index + 1}:`, archivoData);
        archivos.push(archivoData);
      });
    }
    
    console.log("📋 Total archivos adicionales:", archivos.length);
    return archivos;
  };

  // Función para descargar archivo original con logs mejorados
  const descargarArchivoOriginal = async (archivo) => {
    console.log("🔽 Intentando descargar archivo:", archivo);
    
    try {
      // Intentar diferentes URLs disponibles
      let urlParaDescargar = archivo.url;
      
      if (!urlParaDescargar && archivo.infoCompleta) {
        // Buscar en la información completa del archivo
        const info = archivo.infoCompleta;
        const tipo = archivo.tipo;
        
        // Buscar en diferentes patrones de nombres de campos
        urlParaDescargar = info.urls?.[0] || 
                          info.urlsSubidos?.[0] || 
                          info[`${tipo}URLs`]?.[0] ||
                          info.reciboSueldoURLs?.[0] || 
                          info.fotoDNIURLs?.[0] || 
                          info.escriturasURLs?.[0] ||
                          info.url ||
                          info.dataUrl;
        
        console.log("🔄 Buscando URL en información completa:", urlParaDescargar);
      }
      
      // Si aún no tenemos URL, intentar obtenerla desde Firebase Storage
      if (!urlParaDescargar && archivo.storageRef) {
        console.log("🔄 Intentando obtener URL desde Firebase Storage...");
        urlParaDescargar = await obtenerURLDesdeStorage(archivo.storageRef);
      }
      
      if (!urlParaDescargar) {
        console.error("❌ No se encontró URL para el archivo:", archivo.nombre);
        console.log("📊 Información disponible del archivo:", archivo);
        alert(`❌ El archivo "${archivo.nombre}" no tiene URL disponible para descarga.\n\nInformación de debug:\n- Tipo: ${archivo.tipo}\n- Storage Ref: ${archivo.storageRef}\n- Información completa disponible: ${archivo.infoCompleta ? 'SÍ' : 'NO'}\n\nPosibles causas:\n- El archivo fue subido pero no se guardó la URL\n- El archivo no existe en Firebase Storage\n- Permisos insuficientes para acceder al archivo`);
        return;
      }
      
      console.log("📡 URL a usar para descarga:", urlParaDescargar);
      
      if (urlParaDescargar.startsWith('data:')) {
        console.log("💾 Procesando data URL...");
        // Si es un data URL, crear blob y descargar
        const response = await fetch(urlParaDescargar);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = archivo.nombre;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log("✅ Descarga iniciada desde data URL");
      } else if (urlParaDescargar.startsWith('blob:')) {
        console.log("📦 Procesando Blob URL...");
        // Si es una Blob URL, descargar directamente
        const a = document.createElement('a');
        a.href = urlParaDescargar;
        a.download = archivo.nombre;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        console.log("✅ Descarga iniciada desde Blob URL");
      } else if (urlParaDescargar.startsWith('http')) {
        console.log("🌐 Abriendo URL externa...");
        // Si es una URL normal, abrir en nueva pestaña
        window.open(urlParaDescargar, '_blank');
        console.log("✅ Archivo abierto en nueva pestaña");
      } else {
        console.error("❌ Formato de URL no reconocido:", urlParaDescargar);
        alert(`❌ El archivo tiene un formato de URL no válido: ${urlParaDescargar.substring(0, 50)}...`);
      }
    } catch (error) {
      console.error("❌ Error descargando archivo:", error);
      alert(`❌ Error al descargar el archivo: ${error.message}`);
    }
  };

  // useEffect(() => {
  //   if (credito?.id) {
  //     cargarHistorialComentarios();
  //   }
  // }, [credito?.id]);


useEffect(() => {
  if (!credito?.id) return;
  cargarHistorialComentarios(); // carga inicial

  // Suscripción en vivo
  const col = inferColeccion(credito);
  const id  = String(credito?.id || "").trim();
  if (!col || !id) return;

  const unsub = onSnapshot(doc(db, col, id), (snap) => {
    if (!snap.exists()) return;
    const data = snap.data() || {};
    const hist = Array.isArray(data.comentariosHistorial) ? data.comentariosHistorial : [];
    setHistorialComentarios(sortByFechaDesc(hist));
  });
  return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [credito?.id]);


  
  if (!credito) return null;

  const comentariosParaPDF = historialComentarios.length > 0 
    ? historialComentarios 
    : (String(comentarios || "").trim() 
        ? [{ fecha: new Date().toISOString(), texto: String(comentarios).trim() }] 
        : []);

  const archivosOriginales = getArchivosOriginales();
  const archivosAdicionales = getArchivosAdicionales();

useEffect(() => {
     // Convierte la URL del logo (sirve en dev/prod) a base64 para jsPDF
     const toDataURL = async (url) => {
       const res = await fetch(url);
       const blob = await res.blob();
       return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onloadend = () => resolve(reader.result);
         reader.onerror = reject;
         reader.readAsDataURL(blob);
       });
     };
     toDataURL(logo2Url).then(setLogoBase64).catch(() => setLogoBase64(null));
   }, []);


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-xl" onClick={(e) => e.stopPropagation()}>

        <div className="dlg-header">
          <div>
            <h2 className="dlg-title">Detalles del crédito</h2>
            <p className="dlg-subtitle">Información completa del crédito.</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="dlg-grid">

          <aside className="dlg-col-side">
            <div className={`card estado-card estado-${EST.cls}`}>
              <div className="card-header">Estado actual</div>
              <div className="card-body center">
                <div className="estado-icon">{EST.icon}</div>
                <div className="estado-titulo">{EST.titulo}</div>
                <div className="estado-sub">{EST.sub}</div>
{isAdmin && (
                <div className="stack gap" style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => generarCreditoPDF(credito, { comentarios: comentariosParaPDF, logo: logoBase64 })}
                  >
                    <FileText size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Generar PDF
                  </button>
                </div>)}

                {isAdmin && (
                  <div className="stack gap" style={{ marginTop: 10 }}>
                    {onMarcarPagado && (
                      <button className="btn btn-ghost" onClick={() => onMarcarPagado(credito)}>
                        <CheckCircle size={15} style={{ marginRight: 6, flexShrink: 0 }} /> No debe nada
                      </button>
                    )}
                    {onMarcarFaltaPago && (
                      <button className="btn btn-danger" onClick={() => onMarcarFaltaPago(credito)}>
                        <AlertTriangle size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Marcar falta de pago
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">Información adicional</div>
              <div className="card-body">
                <div className="kv"><span className="k">Estado</span><span className="v">{EST.titulo}</span></div>
                <div className="kv"><span className="k">Estado de pago</span><span className="v">{credito.estadoPago || "—"}</span></div>
                <div className="kv"><span className="k">Fecha aprobación</span><span className="v">{credito.fechaAprobacion ? formatDate(credito.fechaAprobacion) : "—"}</span></div>
                <div className="kv"><span className="k">Asignado a</span><span className="v">{credito.agenciaUsuario?.nombre || "—"}</span></div>
              </div>
            </div>

            <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
          </aside>

          <main className="dlg-col-main">
            <div className="card">
              <div className="card-header">Información del solicitante</div>
              <div className="card-body ">
                <div className="kv"><span className="k">Nombre completo</span><span className="v">{credito.nombre || "—"}</span></div>
                <div className="kv"><span className="k">DNI</span><span className="v">{credito.dni || "—"}</span></div>
                <div className="kv"><span className="k">Teléfono</span><span className="v">{credito.telefono || "—"}</span></div>
                <div className="kv"><span className="k">Email</span><span className="v">{credito.email || "—"}</span></div>
                <div className="kv"><span className="k">Ingresos</span><span className="v">{formatCurrency(credito.ingresos)}</span></div>
                <div className="kv"><span className="k">Fecha de solicitud</span><span className="v">{formatDate(credito.fechaCreacion)}</span></div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Detalles del préstamo</div>
              <div className="card-body ">
                <div className="kv"><span className="k">Tipo</span><span className="v">{tipoLegible(credito.tipo)}</span></div>
                <div className="kv"><span className="k">Monto solicitado</span><span className="v">{formatCurrency(credito.montoSolicitado)}</span></div>
                <div className="kv"><span className="k">Plazo</span><span className="v">{(credito.plazo || 0)} meses</span></div>
                <div className="kv"><span className="k">Cuota mensual</span><span className="v">{formatCurrency(credito.cuotaCalculada)}</span></div>
                <div className="kv"><span className="k">Tasa anual</span><span className="v">{credito.tasaAnual ? `${credito.tasaAnual}%` : "—"}</span></div>
                <div className="kv"><span className="k">Total a pagar</span><span className="v">{formatCurrency(credito.totalAPagar)}</span></div>
              </div>
            </div>

            {/* Documentación y gestión de archivos */}
            <div className="card">
              <div className="card-header">Documentación</div>
              <div className="card-body">
                {/* Archivos originales */}
                {archivosOriginales.length > 0 && (
                  <div className="mb-4">
                    <h4 className="doc-section-title">Documentos originales</h4>
                    <ul className="docs-list">
                      {archivosOriginales.map((d, i) => (
                        <li key={i} className="doc-item">
                          <div className="doc-icon"><FileText size={18} /></div>
                          <div className="doc-info">
                            <div className="doc-name">{d.nombre}</div>
                            <div className="doc-size">{d.tipo}</div>
                          </div>
                          <button
                            className="doc-download"
                            onClick={() => descargarArchivoOriginal(d)}
                            title="Descargar archivo"
                          >
                            <Download size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Archivos adicionales subidos */}
                {archivosAdicionales.length > 0 && (
                  <div className="mb-4">
                    <h4 className="doc-section-title">Archivos adicionales</h4>
                    <ul className="docs-list">
                      {archivosAdicionales.map((archivo, i) => (
                        <li key={`adicional-${i}`} className="doc-item">
                          <div className="doc-icon"><Paperclip size={18} /></div>
                          <div className="doc-info">
                            <div className="doc-name">{archivo.nombre}</div>
                            <div className="doc-size">
                              Subido el {formatDate(archivo.fechaSubida)}
                            </div>
                          </div>
                          {archivo.url && (
                            <button
                              className="doc-download"
                              onClick={() => descargarArchivoOriginal(archivo)}
                              title="Descargar archivo"
                            >
                              <Download size={16} />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sección para subir nuevos archivos */}
                <div className="file-upload-section">
                  <h4 className="doc-section-title">Subir archivos</h4>

                  {archivosAdjuntos.length > 0 && (
                    <div className="archivos-adjuntos mb-3">
                      <h5>Archivos seleccionados ({archivosAdjuntos.length}):</h5>
                      {archivosAdjuntos.map((archivo, index) => (
                        <div key={index} className="archivo-adjunto">
                          <span className="archivo-nombre">
                            {archivo.name || archivo.nombre}
                            <small className="file-size">
                              ({(archivo.size / 1024 / 1024).toFixed(2)} MB)
                            </small>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="btn-remove-file"
                            title="Quitar archivo"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="upload-controls">
                    <label htmlFor="file-upload-credito" className="file-upload-label">
                      <FolderOpen size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Seleccionar archivos
                    </label>
                    <input
                      id="file-upload-credito"
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx"
                      onChange={(e) => handleFileChange(e.target.files)}
                      className="file-upload-input"
                    />

                    {archivosAdjuntos.length > 0 && (
                      <button
                        className="btn btn-primary upload-btn"
                        onClick={subirArchivos}
                        disabled={uploading}
                      >
                        <Upload size={15} style={{ marginRight: 6, flexShrink: 0 }} />
                        {uploading ? "Subiendo..." : `Subir ${archivosAdjuntos.length} archivo${archivosAdjuntos.length > 1 ? "s" : ""}`}
                      </button>
                    )}
                  </div>
                  
                  <small className="file-upload-help">
                    PDF, JPG, PNG, TXT, DOC, DOCX (máx. 10MB cada uno)
                  </small>
                </div>

                {archivosOriginales.length === 0 && archivosAdicionales.length === 0 && (
                  <div className="muted">No hay documentos disponibles.</div>
                )}
              </div>
            </div>

            {/* Historial de comentarios */}
            {isAdmin && (
            <div className="card">
              <div className="card-header">
                Historial de comentarios
                {loadingHistorial && <span className="loading-text"> (Cargando...)</span>}
              </div>
              <div className="card-body">
                {historialComentarios.length > 0 ? (
                  <div className="comentarios-historial">
                    {historialComentarios.map((comentario, index) => (
                      <div key={comentario.id || index} className="comentario-item">
                        <div className="comentario-header">
                          <span className="comentario-autor">{comentario.autor || "Sistema"}</span>
                          <span className="comentario-fecha">{formatDate(comentario.fechaCreacion)}</span>
                        </div>
                        <div className="comentario-texto">{comentario.texto}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="muted">No hay comentarios aún.</div>
                )}
              </div>
            </div>
)}
            {/* Agregar nuevo comentario */}
            {isAdmin && (
            <div className="card">
              <div className="card-header">Agregar comentario</div>
              <div className="card-body">
                <textarea
                  className="text-area"
                  rows={4}
                  placeholder="Escribe tu comentario aquí..."
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                />

                <div className="actions-right">
                  <button 
                    className="btn btn-primary" 
                    disabled={saving || !comentarios.trim()} 
                    onClick={guardarComentario}
                  >
                    {saving ? "Guardando..." : <><MessageSquare size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Enviar comentario</>}
                  </button>
                </div>
              </div>
            </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
